/**
 * Data display controls: what an app shows. HeroUI (`@heroui/react`) and HeroUI Pro (`@heroui-pro/react`), by node type.
 * Rows come in literal (`rows`) or bound (`rowsFrom`, with `keys` renaming the bound list's keys to the documented ones).
 * OURS: the workbench defines this file; AIOE mirrors it.
 */
import { UI_LIMITS } from "../uiBase.js";
import { control } from "./kit.js";
const SIZES = ["sm", "md", "lg"];
const STATUS = ["info", "success", "warning", "danger"];
export const GRID_CELL_KINDS = ["text", "number", "money", "date", "chip", "avatar", "progress", "sparkline", "link", "trend"];
export const DISPLAY_CONTROLS = [
    control({
        type: "data-grid",
        group: "data-display",
        heroui: "@heroui-pro/react DataGrid",
        summary: "data-grid { caption?, columns[{ key, label, kind?: text|number|money|date|chip|avatar|progress|sparkline|link|trend, sortable?, filterable?, resizable?, pinned?: start|end, width?, align?: start|center|end, tone?, action? }], rows | rowsFrom, groupBy?, selection?: none|single|multiple, bulkActions?[{ label, action }], rowActions?[{ label, action }], pageSize?, search?, exportCsv?, emptyText? }: sorting, per-column filters, grouping, column resizing and reordering, pinned columns, selection with bulk actions (the action's row carries ids: the selected rows' id column, comma-separated), pages or virtualised rows, cell renderers (chip, avatar, progress 0-100, sparkline from \"1,4,2\", link runs the column's action, trend up/down/flat or a signed number) and CSV export of what is shown. A row's id is its id key (default id).",
        example: {
            type: "data-grid",
            caption: "Open tasks",
            columns: [
                { key: "title", label: "Task", sortable: true, filterable: true, pinned: "start", width: 220 },
                { key: "owner", label: "Owner", kind: "avatar", sortable: true },
                { key: "status", label: "Status", kind: "chip", filterable: true },
                { key: "done", label: "Done", kind: "progress", sortable: true },
                { key: "trend", label: "Last 6 days", kind: "sparkline" },
            ],
            rows: [
                { id: "t1", title: "Ship the release", owner: "Ana", status: "Doing", done: 60, trend: "1,3,2,5,4,6" },
                { id: "t2", title: "Review the plan", owner: "Ben", status: "Waiting", done: 10, trend: "1,1,2,2,2,3" },
            ],
            selection: "multiple",
            bulkActions: [{ label: "Archive", action: { do: "run", name: "archive", confirm: "Archive the selected tasks?" } }],
            search: true,
            exportCsv: true,
        },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({
            caption: k.label.optional(),
            columns: k.z
                .array(k.z
                .object({
                key: k.key,
                label: k.label,
                kind: k.z.enum(GRID_CELL_KINDS).optional(),
                sortable: k.z.boolean().optional(),
                filterable: k.z.boolean().optional(),
                resizable: k.z.boolean().optional(),
                pinned: k.z.enum(["start", "end"]).optional(),
                width: k.z.number().int().min(40).max(800).optional(),
                align: k.z.enum(["start", "center", "end"]).optional(),
                tone: k.tone.optional(),
                action: k.action.optional(),
            })
                .strict())
                .min(1)
                .max(UI_LIMITS.gridColumns),
            rows: k.rows(),
            rowsFrom: k.rowsFrom,
            idKey: k.key.optional(),
            groupBy: k.key.optional(),
            selection: k.z.enum(["none", "single", "multiple"]).optional(),
            bulkActions: k.z.array(k.z.object({ label: k.label.min(1), action: k.action }).strict()).max(UI_LIMITS.rowActions).optional(),
            rowActions: k.z.array(k.z.object({ label: k.label.min(1), action: k.action }).strict()).max(UI_LIMITS.rowActions).optional(),
            pageSize: k.z.number().int().min(5).max(100).optional(),
            search: k.z.boolean().optional(),
            exportCsv: k.z.boolean().optional(),
            emptyText: k.label.optional(),
            height: k.z.number().int().min(160).max(720).optional(),
        }),
    }),
    control({
        type: "list-view",
        group: "data-display",
        heroui: "@heroui-pro/react ListView",
        summary: "list-view { caption?, rows | rowsFrom (each { id?, title, description?, icon? }), keys?, selection?: none|single|multiple, name?, rowActions?[{ label, action }], onOpen? } — a list of rows; a press on a row runs onOpen with the row; selected ids go to field `name`",
        example: {
            type: "list-view",
            caption: "Recent files",
            rows: [
                { id: "a", title: "plan.md", description: "Edited today" },
                { id: "b", title: "notes.md", description: "Edited yesterday" },
            ],
            onOpen: { do: "run", name: "open_file" },
        },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({
            caption: k.label.optional(),
            rows: k.rows(),
            rowsFrom: k.rowsFrom,
            keys: k.keys,
            selection: k.z.enum(["none", "single", "multiple"]).optional(),
            name: k.name.optional(),
            rowActions: k.z.array(k.z.object({ label: k.label.min(1), action: k.action }).strict()).max(UI_LIMITS.rowActions).optional(),
            onOpen: k.action.optional(),
        }),
        field: "options",
        named: true,
    }),
    control({
        type: "item-card",
        group: "data-display",
        heroui: "@heroui-pro/react ItemCard",
        summary: "item-card { title, description?, icon?, tone?, action?: { label, action } } — one row-like card with an icon, a title and a button",
        example: { type: "item-card", title: "Release checklist", description: "Four steps left", icon: "sparkles", tone: "teal", action: { label: "Open", action: { do: "open-view", view: "tasks" } } },
        shape: (k) => ({ title: k.label.min(1), description: k.hint.optional(), icon: k.z.string().max(40).optional(), tone: k.tone.optional(), action: k.z.object({ label: k.label.min(1), action: k.action }).strict().optional() }),
    }),
    control({
        type: "item-card-group",
        group: "data-display",
        heroui: "@heroui-pro/react ItemCardGroup",
        summary: "item-card-group { title?, description?, children[] (item-cards) | rows | rowsFrom (each { title, description?, icon? }), keys? } — item-cards under one heading",
        example: {
            type: "item-card-group",
            title: "Connected",
            description: "What this app reads",
            rows: [
                { title: "Tasks", description: "The board" },
                { title: "Pull requests", description: "From GitHub" },
            ],
        },
        shape: (k) => ({ title: k.label.optional(), description: k.hint.optional(), children: k.nodes().optional(), rows: k.rows(), rowsFrom: k.rowsFrom, keys: k.keys }),
    }),
    control({
        type: "kpi",
        group: "data-display",
        heroui: "@heroui-pro/react/kpi KPI",
        summary: "kpi { label, value, trend?: { value, direction?: up|down|flat, good?: up|down }, points?: number[] (an inline chart), progress?: 0-100, hint?, icon?, tone? } — a headline number with its trend",
        example: { type: "kpi", label: "Open pull requests", value: "14", trend: { value: "+3", direction: "up", good: "down" }, points: [4, 6, 5, 9, 11, 14], tone: "blue" },
        shape: (k) => ({
            label: k.label.min(1),
            value: k.z.union([k.z.string().max(UI_LIMITS.label), k.num]),
            trend: k.z.object({ value: k.z.union([k.z.string().max(40), k.num]), direction: k.z.enum(["up", "down", "flat"]).optional(), good: k.z.enum(["up", "down"]).optional() }).strict().optional(),
            points: k.numbers(48).optional(),
            progress: k.z.number().min(0).max(100).optional(),
            hint: k.hint.optional(),
            icon: k.z.string().max(40).optional(),
            tone: k.tone.optional(),
        }),
    }),
    control({
        type: "kpi-group",
        group: "data-display",
        heroui: "@heroui-pro/react KPIGroup",
        summary: "kpi-group { children[] (kpis) } — kpis side by side with separators",
        example: { type: "kpi-group", children: [{ type: "kpi", label: "Open", value: 14 }, { type: "kpi", label: "Merged", value: 52, trend: { value: "+8", direction: "up" } }] },
        shape: (k) => ({ children: k.nodes(6) }),
    }),
    control({
        type: "widget",
        group: "data-display",
        heroui: "@heroui-pro/react Widget",
        summary: "widget { title, description?, children[], footer?, legend?[{ label, tone? }] } — a titled panel for a chart or a figure, with a legend",
        example: { type: "widget", title: "Builds this week", description: "Passing and failing", footer: "Updated just now", legend: [{ label: "Passing", tone: "teal" }, { label: "Failing", tone: "rose" }], children: [{ type: "text", text: "28 builds, 3 failed." }] },
        shape: (k) => ({ title: k.label.min(1), description: k.hint.optional(), children: k.nodes(), footer: k.hint.optional(), legend: k.z.array(k.z.object({ label: k.label.min(1), tone: k.tone.optional() }).strict()).max(UI_LIMITS.chartSeries).optional() }),
    }),
    control({
        type: "timeline",
        group: "data-display",
        heroui: "@heroui-pro/react Timeline",
        summary: "timeline { rows | rowsFrom (each { title, time?, description?, status?: done|current|upcoming|failed }), keys? } — events down a rail, oldest first",
        example: {
            type: "timeline",
            rows: [
                { title: "Branch cut", time: "Mon", status: "done" },
                { title: "Release candidate", time: "Wed", status: "current", description: "Waiting on tests" },
                { title: "Publish", time: "Fri", status: "upcoming" },
            ],
        },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ rows: k.rows(), rowsFrom: k.rowsFrom, keys: k.keys }),
    }),
    control({
        type: "agenda",
        group: "data-display",
        heroui: "@heroui-pro/react Agenda",
        summary: "agenda { rows | rowsFrom (each { id?, title, start: ISO date-time, end?, allDay?, tone? }), keys?, view?: day|week|month } — a calendar of events",
        example: {
            type: "agenda",
            view: "week",
            rows: [
                { id: "e1", title: "Planning", start: "2026-10-26T10:00:00", end: "2026-10-26T11:00:00", tone: "violet" },
                { id: "e2", title: "Release day", start: "2026-10-30", allDay: true, tone: "amber" },
            ],
        },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ rows: k.rows(), rowsFrom: k.rowsFrom, keys: k.keys, view: k.z.enum(["day", "week", "month"]).optional() }),
    }),
    control({
        type: "kanban",
        group: "data-display",
        heroui: "@heroui-pro/react Kanban",
        summary: "kanban { columns[{ id, label, tone? }] | columnsFrom, rows | rowsFrom (each { id, title, description?, column, tag? }), keys?, onMove?: action } — cards in columns; dragging a card to another column runs onMove with the row { id, column }",
        example: {
            type: "kanban",
            columns: [{ id: "todo", label: "To do" }, { id: "doing", label: "Doing", tone: "blue" }, { id: "done", label: "Done", tone: "green" }],
            rows: [
                { id: "1", title: "Write the brief", column: "doing" },
                { id: "2", title: "Review", column: "todo", tag: "urgent" },
            ],
            onMove: { do: "run", name: "move_task" },
        },
        sources: [["rows", "rowsFrom"], ["columns", "columnsFrom"]],
        shape: (k) => ({
            columns: k.z.array(k.z.object({ id: k.z.string().regex(/^[A-Za-z0-9_-]{1,40}$/), label: k.label.min(1), tone: k.tone.optional() }).strict()).max(8).optional(),
            columnsFrom: k.path.optional(),
            rows: k.rows(),
            rowsFrom: k.rowsFrom,
            keys: k.keys,
            onMove: k.action.optional(),
        }),
    }),
    control({
        type: "file-tree",
        group: "data-display",
        heroui: "@heroui-pro/react FileTree",
        summary: "file-tree { rows | rowsFrom (each { path: \"src/app.ts\", kind?: file|folder }), keys?, onOpen?: action } — folders and files from paths; Open runs onOpen with the row { path }",
        example: { type: "file-tree", rows: [{ path: "src/index.ts" }, { path: "src/ui/view.tsx" }, { path: "README.md" }], onOpen: { do: "run", name: "open_file" } },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ rows: k.rows(), rowsFrom: k.rowsFrom, keys: k.keys, onOpen: k.action.optional() }),
    }),
    control({
        type: "empty-state",
        group: "data-display",
        heroui: "@heroui-pro/react EmptyState",
        summary: "empty-state { title, description?, icon?, buttons?[] } — what to say when there is nothing yet",
        example: { type: "empty-state", title: "No tasks yet", description: "Add one and it shows here.", icon: "sparkles", buttons: [{ type: "button", label: "Add a task", variant: "primary", action: { do: "open-view", view: "tasks" } }] },
        shape: (k) => ({ title: k.label.min(1), description: k.hint.optional(), icon: k.z.string().max(40).optional(), buttons: k.nodes(UI_LIMITS.buttons).optional() }),
    }),
    control({
        type: "carousel",
        group: "data-display",
        heroui: "@heroui-pro/react/carousel Carousel",
        summary: "carousel { panels[{ id, label, children[] }] } — slides of components with arrows and dots",
        example: { type: "carousel", panels: [{ id: "one", label: "Step one", children: [{ type: "text", text: "Pick a repository." }] }, { id: "two", label: "Step two", children: [{ type: "text", text: "Say what you want." }] }] },
        shape: (k) => ({ panels: k.panels() }),
    }),
    control({
        type: "hover-card",
        group: "data-display",
        heroui: "@heroui-pro/react HoverCard",
        summary: "hover-card { label, children[] } — a word that shows a card of components on hover or focus",
        example: { type: "hover-card", label: "Release 0.89.18", children: [{ type: "text", text: "Ships the Apps page." }] },
        shape: (k) => ({ label: k.label.min(1), children: k.nodes() }),
    }),
    control({
        type: "holo-card",
        group: "data-display",
        heroui: "@heroui-pro/react HoloCard",
        summary: "holo-card { title?, children[], tone? } — a card with a holographic sheen that follows the pointer",
        example: { type: "holo-card", title: "Streak", tone: "violet", children: [{ type: "stat", label: "Days", value: 12 }] },
        shape: (k) => ({ title: k.label.optional(), children: k.nodes(), tone: k.tone.optional() }),
    }),
    control({
        type: "trend-chip",
        group: "data-display",
        heroui: "@heroui-pro/react TrendChip",
        summary: "trend-chip { value, direction?: up|down|flat, good?: up|down } — a small up or down figure, green when it moved the good way",
        example: { type: "trend-chip", value: "+12%", direction: "up" },
        shape: (k) => ({ value: k.z.union([k.z.string().max(40), k.num]), direction: k.z.enum(["up", "down", "flat"]).optional(), good: k.z.enum(["up", "down"]).optional() }),
    }),
    control({
        type: "number-value",
        group: "data-display",
        heroui: "@heroui-pro/react NumberValue",
        summary: "number-value { value, format?: number|currency|percent|compact, currency?: AUD, decimals?, label? } — a formatted number",
        example: { type: "number-value", value: 12840.5, format: "currency", currency: "AUD", label: "Spend this month" },
        shape: (k) => ({ value: k.z.union([k.z.string().max(40), k.num]), format: k.z.enum(["number", "currency", "percent", "compact"]).optional(), currency: k.z.string().regex(/^[A-Z]{3}$/).optional(), decimals: k.z.number().int().min(0).max(6).optional(), label: k.label.optional() }),
    }),
    control({
        type: "rating",
        group: "data-display",
        heroui: "@heroui-pro/react Rating",
        summary: "rating { value, max?: 3-10, name? } — stars; read-only unless it has a name, then a field the person sets",
        example: { type: "rating", value: 4, max: 5 },
        shape: (k) => ({ value: k.z.union([k.z.number().min(0).max(10), k.z.string().max(40)]), max: k.z.number().int().min(3).max(10).optional(), name: k.name.optional() }),
        field: "number",
        named: true,
    }),
    control({
        type: "meter",
        group: "data-display",
        heroui: "@heroui/react Meter",
        summary: "meter { label?, value, min?, max?, valueLabel?, tone? } — a gauge bar for a value in a range (disk used, score)",
        example: { type: "meter", label: "Disk used", value: 72, min: 0, max: 100, tone: "amber" },
        shape: (k) => ({ label: k.label.optional(), value: k.z.union([k.num, k.z.string().max(40)]), min: k.num.optional(), max: k.num.optional(), valueLabel: k.label.optional(), tone: k.tone.optional() }),
    }),
    control({
        type: "progress-circle",
        group: "data-display",
        heroui: "@heroui/react ProgressCircle",
        summary: "progress-circle { label?, value, max?, tone? } — a ring of progress with its percentage",
        example: { type: "progress-circle", label: "Tests", value: 64, max: 100, tone: "teal" },
        shape: (k) => ({ label: k.label.optional(), value: k.z.union([k.num, k.z.string().max(40)]), max: k.z.number().positive().optional(), tone: k.tone.optional() }),
    }),
    control({
        type: "spinner",
        group: "data-display",
        heroui: "@heroui/react Spinner",
        summary: "spinner { label?, size?: sm|md|lg } — working on it",
        example: { type: "spinner", label: "Reading the board", size: "md" },
        shape: (k) => ({ label: k.label.optional(), size: k.z.enum(SIZES).optional() }),
    }),
    control({
        type: "skeleton",
        group: "data-display",
        heroui: "@heroui/react Skeleton",
        summary: "skeleton { shape?: text|line|avatar|card, lines?: 1-8 } — a placeholder while something loads",
        example: { type: "skeleton", shape: "text", lines: 3 },
        shape: (k) => ({ shape: k.z.enum(["text", "line", "avatar", "card"]).optional(), lines: k.z.number().int().min(1).max(8).optional() }),
    }),
    control({
        type: "alert",
        group: "data-display",
        heroui: "@heroui/react Alert",
        summary: "alert { title?, text, status?: info|success|warning|danger } — a notice",
        example: { type: "alert", title: "Heads up", text: "Two checks are still running.", status: "warning" },
        shape: (k) => ({ title: k.label.optional(), text: k.hint.min(1), status: k.z.enum(STATUS).optional() }),
    }),
    control({
        type: "badge",
        group: "data-display",
        heroui: "@heroui/react Badge",
        summary: "badge { text?, children?[] (the thing it sits on, usually an avatar), tone? } — a count or dot on a corner of something",
        example: { type: "badge", text: "3", tone: "rose", children: [{ type: "avatar", name: "Ana Lopez" }] },
        shape: (k) => ({ text: k.z.string().max(8).optional(), children: k.nodes(1).optional(), tone: k.tone.optional() }),
    }),
    control({
        type: "avatar",
        group: "data-display",
        heroui: "@heroui/react Avatar",
        summary: "avatar { name, size?: sm|md|lg, tone? } — a person's initials in a circle (no pictures: an app loads no images)",
        example: { type: "avatar", name: "Ana Lopez", size: "md", tone: "violet" },
        shape: (k) => ({ name: k.label.min(1), size: k.z.enum(SIZES).optional(), tone: k.tone.optional() }),
    }),
    control({
        type: "avatar-group",
        group: "data-display",
        heroui: "@heroui/react AvatarGroup",
        summary: "avatar-group { rows | rowsFrom (each { name, tone? }), keys?, max? } — overlapping avatars with a +N",
        example: { type: "avatar-group", rows: [{ name: "Ana Lopez" }, { name: "Ben Ito" }, { name: "Cy Wu" }, { name: "Di Park" }], max: 3 },
        sources: [["rows", "rowsFrom"]],
        shape: (k) => ({ rows: k.rows(24), rowsFrom: k.rowsFrom, keys: k.keys, max: k.z.number().int().min(1).max(12).optional() }),
    }),
    control({
        type: "kbd",
        group: "data-display",
        heroui: "@heroui/react Kbd",
        summary: "kbd { keys[] } — a keyboard shortcut, one key cap per entry (Ctrl, K)",
        example: { type: "kbd", keys: ["Ctrl", "K"] },
        shape: (k) => ({ keys: k.z.array(k.z.string().min(1).max(12)).min(1).max(5) }),
    }),
    control({
        type: "tooltip",
        group: "data-display",
        heroui: "@heroui/react Tooltip",
        summary: "tooltip { text, children[] } — the same as the tooltip property any component takes, around several components",
        example: { type: "tooltip", text: "Counts open items", children: [{ type: "chip", text: "Open" }] },
        shape: (k) => ({ text: k.label.min(1), children: k.nodes() }),
    }),
    control({
        type: "typography",
        group: "data-display",
        heroui: "@heroui/react Typography",
        summary: "typography { text, variant?: h1|h2|h3|h4|body|lead|small|muted|code, tone? } — text in the type scale",
        example: { type: "typography", text: "A short introduction to the board", variant: "lead" },
        shape: (k) => ({ text: k.text, variant: k.z.enum(["h1", "h2", "h3", "h4", "body", "lead", "small", "muted", "code"]).optional(), tone: k.tone.optional() }),
    }),
    control({
        type: "markdown",
        group: "data-display",
        heroui: "@heroui-pro/react/markdown Markdown",
        summary: "markdown { text } — formatted text from Markdown (headings, lists, tables, emphasis; no raw HTML, no images, links are shown but never followed)",
        example: { type: "markdown", text: "## Notes\n\n- **Fast** to read\n- Lists and `code`\n" },
        shape: (k) => ({ text: k.text }),
    }),
    control({
        type: "code-block",
        group: "data-display",
        heroui: "@heroui-pro/react/code-block CodeBlock",
        summary: "code-block { code, language?, title? } — code with highlighting and a copy button (shown, never run)",
        example: { type: "code-block", title: "Install", language: "bash", code: "npm install\nnpm test" },
        shape: (k) => ({ code: k.z.string().max(UI_LIMITS.text), language: k.z.string().regex(/^[a-z0-9+#-]{1,20}$/).optional(), title: k.label.optional() }),
    }),
    control({
        type: "chain-of-thought",
        group: "data-display",
        heroui: "@heroui-pro/react ChainOfThought",
        summary: "chain-of-thought { steps | stepsFrom (each { title, detail?, state?: done|running|todo }) } — the reasoning or the steps taken, collapsible",
        example: { type: "chain-of-thought", steps: [{ title: "Read the board", state: "done", detail: "14 open tasks" }, { title: "Group by owner", state: "running" }] },
        sources: [["steps", "stepsFrom"]],
        shape: (k) => ({ steps: k.z.array(k.z.object({ title: k.label.min(1), detail: k.hint.optional(), state: k.z.enum(["done", "running", "todo"]).optional() }).strict()).max(UI_LIMITS.steps).optional(), stepsFrom: k.path.optional() }),
    }),
    control({
        type: "text-shimmer",
        group: "data-display",
        heroui: "@heroui-pro/react TextShimmer",
        summary: "text-shimmer { text } — text with a moving sheen, for \"working on it\"",
        example: { type: "text-shimmer", text: "Thinking it through" },
        shape: (k) => ({ text: k.label.min(1) }),
    }),
    control({
        type: "color-swatch",
        group: "data-display",
        heroui: "@heroui/react ColorSwatch",
        summary: "color-swatch { color: #rrggbb, label?, size?: sm|md|lg } — a square of colour",
        example: { type: "color-swatch", color: "#2f9e8f", label: "Brand teal", size: "md" },
        shape: (k) => ({ color: k.color, label: k.label.optional(), size: k.z.enum(SIZES).optional() }),
    }),
];
//# sourceMappingURL=display.js.map